// ./src/i18n/index.ts
import i18n from "i18next";
import { initReactI18next } from "react-i18next";

// ============================================
// English — bundled statically (default/fallback)
// ============================================

import enCommon from "./locales/en/common.json";
import enCars from "./locales/en/cars.json";
import enValidation from "./locales/en/validation.json";
import enRefuels from "./locales/en/refuels.json";
import enExpenses from "./locales/en/expenses.json";
import enCheckpoints from "./locales/en/checkpoints.json";
import enTags from "./locales/en/tags.json";
import enServices from "./locales/en/services.json";
import enServiceIntervals from "./locales/en/serviceIntervals.json";
import enReports from "./locales/en/reports.json";
import enGlovebox from "./locales/en/glovebox.json";
import enRevenues from "./locales/en/revenues.json";
import enTravels from "./locales/en/travels.json";
import enWeather from "./locales/en/weather.json";
import enExpenseSchedules from "./locales/en/expenseSchedules.json";
import enVehicleRecalls from "./locales/en/vehicleRecalls.json";
import enVehicleFinancing from "./locales/en/vehicleFinancing.json";
import enTireSets from "./locales/en/tireSets.json";
import enVehicleTasks from "./locales/en/vehicleTasks.json";
import enSupport from "./locales/en/support.json";
import enParking from "./locales/en/parking.json";
import enParkingSessions from "./locales/en/parkingSessions.json";
import enLogbook from "./locales/en/logbook.json";
import enSavedPlaces from "./locales/en/savedPlaces.json";

// ============================================
// Types
// ============================================

export type SupportedLanguage = "en" | "ru" | "fr" | "es";

export const SUPPORTED_LANGUAGES: SupportedLanguage[] = [
  "en",
  "ru",
  "fr",
  "es",
];

export const LANGUAGE_NAMES: Record<SupportedLanguage, string> = {
  en: "English",
  ru: "Русский",
  fr: "Français",
  es: "Español",
};

export const DEFAULT_LANGUAGE: SupportedLanguage = "en";

const ALL_NAMESPACES = [
  "common",
  "cars",
  "validation",
  "refuels",
  "expenses",
  "checkpoints",
  "tags",
  "services",
  "serviceIntervals",
  "reports",
  "glovebox",
  "revenues",
  "travels",
  "weather",
  "expenseSchedules",
  "vehicleRecalls",
  "vehicleFinancing",
  "tireSets",
  "vehicleTasks",
  "support",
  "parking",
  "parkingSessions",
  "logbook",
  "savedPlaces",
] as const;

export type AppNamespace = (typeof ALL_NAMESPACES)[number];

// ============================================
// Dynamic language loaders (RU/FR/ES)
// ============================================

const languageLoaders: Record<
  Exclude<SupportedLanguage, "en">,
  () => Promise<Record<string, Record<string, unknown>>>
> = {
  ru: async () => {
    const modules = await Promise.all([
      import("./locales/ru/common.json"),
      import("./locales/ru/cars.json"),
      import("./locales/ru/validation.json"),
      import("./locales/ru/refuels.json"),
      import("./locales/ru/expenses.json"),
      import("./locales/ru/checkpoints.json"),
      import("./locales/ru/tags.json"),
      import("./locales/ru/services.json"),
      import("./locales/ru/serviceIntervals.json"),
      import("./locales/ru/reports.json"),
      import("./locales/ru/glovebox.json"),
      import("./locales/ru/revenues.json"),
      import("./locales/ru/travels.json"),
      import("./locales/ru/weather.json"),
      import("./locales/ru/expenseSchedules.json"),
      import("./locales/ru/vehicleRecalls.json"),
      import("./locales/ru/vehicleFinancing.json"),
      import("./locales/ru/tireSets.json"),
      import("./locales/ru/vehicleTasks.json"),
      import("./locales/ru/support.json"),
      import("./locales/ru/parking.json"),
      import("./locales/ru/parkingSessions.json"),
      import("./locales/ru/logbook.json"),
      import("./locales/ru/savedPlaces.json"),
    ]);

    const result: Record<string, Record<string, unknown>> = {};
    ALL_NAMESPACES.forEach((ns, i) => {
      result[ns] = modules[i].default;
    });
    return result;
  },
  fr: async () => {
    const modules = await Promise.all([
      import("./locales/fr/common.json"),
      import("./locales/fr/cars.json"),
      import("./locales/fr/validation.json"),
      import("./locales/fr/refuels.json"),
      import("./locales/fr/expenses.json"),
      import("./locales/fr/checkpoints.json"),
      import("./locales/fr/tags.json"),
      import("./locales/fr/services.json"),
      import("./locales/fr/serviceIntervals.json"),
      import("./locales/fr/reports.json"),
      import("./locales/fr/glovebox.json"),
      import("./locales/fr/revenues.json"),
      import("./locales/fr/travels.json"),
      import("./locales/fr/weather.json"),
      import("./locales/fr/expenseSchedules.json"),
      import("./locales/fr/vehicleRecalls.json"),
      import("./locales/fr/vehicleFinancing.json"),
      import("./locales/fr/tireSets.json"),
      import("./locales/fr/vehicleTasks.json"),
      import("./locales/fr/support.json"),
      import("./locales/fr/parking.json"),
      import("./locales/fr/parkingSessions.json"),
      import("./locales/fr/logbook.json"),
      import("./locales/fr/savedPlaces.json"),
    ]);
    const result: Record<string, Record<string, unknown>> = {};
    ALL_NAMESPACES.forEach((ns, i) => {
      result[ns] = modules[i].default;
    });
    return result;
  },
  es: async () => {
    const modules = await Promise.all([
      import("./locales/es/common.json"),
      import("./locales/es/cars.json"),
      import("./locales/es/validation.json"),
      import("./locales/es/refuels.json"),
      import("./locales/es/expenses.json"),
      import("./locales/es/checkpoints.json"),
      import("./locales/es/tags.json"),
      import("./locales/es/services.json"),
      import("./locales/es/serviceIntervals.json"),
      import("./locales/es/reports.json"),
      import("./locales/es/glovebox.json"),
      import("./locales/es/revenues.json"),
      import("./locales/es/travels.json"),
      import("./locales/es/weather.json"),
      import("./locales/es/expenseSchedules.json"),
      import("./locales/es/vehicleRecalls.json"),
      import("./locales/es/vehicleFinancing.json"),
      import("./locales/es/tireSets.json"),
      import("./locales/es/vehicleTasks.json"),
      import("./locales/es/support.json"),
      import("./locales/es/parking.json"),
      import("./locales/es/parkingSessions.json"),
      import("./locales/es/logbook.json"),
      import("./locales/es/savedPlaces.json"),
    ]);
    const result: Record<string, Record<string, unknown>> = {};
    ALL_NAMESPACES.forEach((ns, i) => {
      result[ns] = modules[i].default;
    });
    return result;
  },
};

// Track loaded languages to avoid duplicate loads
const loadedLanguages = new Set<SupportedLanguage>(["en"]);

// ============================================
// Public API
// ============================================

/**
 * Load a language and switch to it.
 * English switches instantly (already bundled).
 * Other languages are loaded dynamically, then activated.
 *
 * @returns true if the language was loaded and activated successfully
 */
export const loadLanguage = async (language: string): Promise<boolean> => {
  if (!isLanguageSupported(language)) {
    return false;
  }

  if (language === "en") {
    await i18n.changeLanguage("en");
    return true;
  }

  if (!loadedLanguages.has(language)) {
    const loader = languageLoaders[language];
    const bundles = await loader();

    for (const ns of ALL_NAMESPACES) {
      i18n.addResourceBundle(language, ns, bundles[ns], true, true);
    }

    loadedLanguages.add(language);
  }

  await i18n.changeLanguage(language);
  return true;
};

// ============================================
// Resources — English only
// ============================================

const resources = {
  en: {
    common: enCommon,
    cars: enCars,
    validation: enValidation,
    refuels: enRefuels,
    expenses: enExpenses,
    checkpoints: enCheckpoints,
    tags: enTags,
    services: enServices,
    serviceIntervals: enServiceIntervals,
    reports: enReports,
    glovebox: enGlovebox,
    revenues: enRevenues,
    travels: enTravels,
    weather: enWeather,
    expenseSchedules: enExpenseSchedules,
    vehicleRecalls: enVehicleRecalls,
    vehicleFinancing: enVehicleFinancing,
    tireSets: enTireSets,
    vehicleTasks: enVehicleTasks,
    support: enSupport,
    parking: enParking,
    parkingSessions: enParkingSessions,
    logbook: enLogbook,
    savedPlaces: enSavedPlaces,
  },
};

// ============================================
// Initialize i18n
// ============================================

i18n.use(initReactI18next).init({
  resources,
  lng: DEFAULT_LANGUAGE,
  fallbackLng: DEFAULT_LANGUAGE,
  defaultNS: "common",
  ns: [...ALL_NAMESPACES],

  interpolation: {
    escapeValue: false,
  },

  react: {
    useSuspense: false,
  },
});

// ============================================
// Helper Functions
// ============================================

export const getCurrentLanguage = (): SupportedLanguage => {
  return (i18n.language as SupportedLanguage) || DEFAULT_LANGUAGE;
};

export const isLanguageSupported = (
  language: string
): language is SupportedLanguage => {
  return SUPPORTED_LANGUAGES.includes(language as SupportedLanguage);
};

/**
 * Change the current language.
 * Wraps loadLanguage for backward compatibility.
 * @param language - Language code to switch to
 */
export const changeLanguage = async (language: string): Promise<void> => {
  await loadLanguage(language);
};

export default i18n;